import React, {
  useEffect,
  useState,
} from 'react';

import {
  Modal,
  Table,
  Button,
  DatePicker,
  TimePicker,
  message,
} from 'antd';

import dayjs from 'dayjs';

import { useLocation } from 'react-router-dom';

import { TrainingsApi } from '../../Api/TrainingsApi';
import { toastSuccess, toastError } from '../Toast/ToastComponent';
import { useAuth } from '../../Context/AuthContext';

const AttendancePopup = ({
  visible,
  onClose,
  attendanceData = null,
}) => {
  const { authState } = useAuth();
  const location = useLocation();

  // ================= GET TRAINING ID =================

  const overviewData =
    location?.state?.overviewData || {};

  const trainingId =
    overviewData?.training_details
      ?.id;

  // ================= STATES =================

  const [loading, setLoading] =
    useState(false);

  const [submitLoading, setSubmitLoading] =
    useState(false);

  const [trainees, setTrainees] =
    useState([]);

  const [trainers, setTrainers] =
    useState([]);

  const [attendance, setAttendance] =
    useState({});

  const [sessionDate, setSessionDate] =
    useState(dayjs());

  const [checkInTime, setCheckInTime] =
    useState(
      dayjs('10:00', 'HH:mm')
    );

  const [
    checkOutTime,
    setCheckOutTime,
  ] = useState(
    dayjs('12:00', 'HH:mm')
  );

  // ================= EDIT MODE =================

  const isEdit =
    !!attendanceData;

  // ================= FETCH / INITIALIZE ATTENDANCE =================

  useEffect(() => {
    if (visible && trainingId) {
      if (attendanceData) {
        handlePopulate(attendanceData);
      } else {
        initializeMarkAttendance();
      }
    }
  }, [visible, trainingId]);

  const initializeMarkAttendance = () => {
    const trainersData = (overviewData.trainers || []).map((x) => ({
      id: x.id || x.user_id,
      name: x.name || x.username || 'N/A',
      department: x.department || 'N/A',
      role: 'trainer'
    }));

    const traineesData = (overviewData.trainees || []).map((x) => ({
      id: x.id || x.user_id,
      name: x.name || x.username || 'N/A',
      department: x.department || 'N/A',
      role: 'trainee'
    }));

    setTrainers(trainersData);
    setTrainees(traineesData);

    // Default to present for everyone
    const initialAttendance = {};
    [...trainersData, ...traineesData].forEach((item) => {
      initialAttendance[item.id] = 'present';
    });

    setAttendance(initialAttendance);
    setSessionDate(dayjs());
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);

      const payload = {
        training_id: trainingId,
        session_date: sessionDate.format('YYYY-MM-DD'),
      };

      const response = await TrainingsApi.getDayWiseAttendance(payload);

      if (response.data.success) {
        handlePopulate(response.data.result);
      } else {
        toastError(response.data.message);
      }
    } catch (error) {
      console.log(error);
      toastError('Failed to fetch attendance');
    } finally {
      setLoading(false);
    }
  };

  const handlePopulate = (data) => {
    if (!data) return;

    const trainersData = (data.trainers || []).map((x) => ({
      id: x.user_id,
      name: x.username || 'N/A',
      department: x.department || 'N/A',
      role: 'trainer'
    }));

    const traineesData = (data.trainees || []).map((x) => ({
      id: x.user_id,
      name: x.username || 'N/A',
      department: x.department || 'N/A',
      role: 'trainee'
    }));

    setTrainers(trainersData);
    setTrainees(traineesData);

    // Map attendance status
    const editAttendance = {};
    
    [...(data.trainers || []), ...(data.trainees || [])].forEach((item) => {
      editAttendance[item.user_id] = item.status || 'present';
    });

    setAttendance(editAttendance);

    if (data.session_date) {
      setSessionDate(dayjs(data.session_date));
    }

    const cIn = data.check_in_time;
    const cOut = data.check_out_time;

    setCheckInTime(cIn ? dayjs(cIn, ['HH:mm:ss', 'HH:mm']) : null);
    setCheckOutTime(cOut ? dayjs(cOut, ['HH:mm:ss', 'HH:mm']) : null);
  };

  // ================= CHANGE STATUS =================

  const handleAttendanceChange = (
    userId,
    value
  ) => {
    setAttendance((prev) => ({
      ...prev,

      [userId]: value,
    }));
  };

  // ================= SUBMIT =================

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      const payload = {
        training_id: trainingId,
        trainer_id: authState?.id,
        session_date: sessionDate.format('YYYY-MM-DD'),
        check_in_time: checkInTime?.format('HH:mm'),
        check_out_time: checkOutTime?.format('HH:mm'),

        attendance: trainees.map((item) => ({
          user_id: item.id,
          status: attendance[item.id] || 'present',
          role: item.role
        })),
      };

      // ================= CREATE / UPDATE =================

      const response = isEdit
        ? await TrainingsApi.updateAttendance(payload)
        : await TrainingsApi.markAttendance(payload);

      if (response.data.success) {
        toastSuccess(
          isEdit
            ? 'Attendance updated successfully'
            : 'Attendance marked successfully'
        );

        // Call overall attendance API after success
        try {
          const overallPayload = {
            training_id: trainingId,
            user_id: authState?.id,
          };
          await TrainingsApi.getOverallAttendance(overallPayload);
        } catch (error) {
          console.error('Failed to fetch overall attendance:', error);
        }

        onClose(true);
      } else {
        toastError(response.data.message);
      }
    } catch (error) {
      console.log(error);

      toastError(
        isEdit
          ? 'Failed to update attendance'
          : 'Failed to mark attendance'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // ================= STATUS BUTTON =================

  const StatusButton = ({
    active,
    type,
    onClick,
  }) => {
    const isPresent =
      type === 'present';

    return (
      <button
        onClick={onClick}
        style={{
          border: 'none',

          padding: '6px 14px',

          borderRadius: 6,

          fontSize: 13,

          fontWeight: 500,

          cursor: 'pointer',

          transition: '0.2s',

          background: active
            ? isPresent
              ? '#d1fae5'
              : '#fee2e2'
            : '#f3f4f6',

          color: active
            ? isPresent
              ? '#10b981'
              : '#ef4444'
            : '#9ca3af',
        }}
      >
        {isPresent
          ? 'Present'
          : 'Absent'}
      </button>
    );
  };

  // ================= TABLE COLUMNS =================

  const columns = [
    {
      title: 'Employee Name',

      dataIndex: 'name',
    },

    {
      title:
        'Attendance Status',

      render: (_, record) => (
        <div
          style={{
            display: 'flex',
            gap: 8,
          }}
        >
          {/* PRESENT */}

          <StatusButton
            type="present"
            active={
              attendance[
                record.id
              ] === 'present'
            }
            onClick={() =>
              handleAttendanceChange(
                record.id,
                'present'
              )
            }
          />

          {/* ABSENT */}

          <StatusButton
            type="absent"
            active={
              attendance[
                record.id
              ] === 'absent'
            }
            onClick={() =>
              handleAttendanceChange(
                record.id,
                'absent'
              )
            }
          />
        </div>
      ),
    },
  ];

  // ================= TABLE DATA =================

  const tableData = trainees.map(
    (item) => ({
      id: item.id,

      key: item.id,

      name: item.name,

      department:
        item.department,
      
      role: item.role,
    })
  );

  return (
    <Modal
      title={
        isEdit
          ? 'Update Attendance'
          : 'Mark Attendance'
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      {/* ================= TOP ================= */}

      <div
        style={{
          display: 'flex',
          gap: 16,
          marginBottom: 20,
        }}
      >
        {/* DATE */}

        {/* <div>
          <div
            style={{
              marginBottom: 6,
              fontWeight: 600,
            }}
          >
            Session Date
          </div>

          <DatePicker
            value={sessionDate}
            onChange={(value) =>
              setSessionDate(
                value
              )
            }
          />
        </div> */}

        {/* CHECK IN */}

    {/* CHECK IN */}

<div>
  <div
    style={{
      marginBottom: 6,
      fontWeight: 600,
    }}
  >
    Check In
  </div>

  <TimePicker
    format="HH:mm"
    value={checkInTime}
    needConfirm={false}
    onChange={(value) => setCheckInTime(value)}
  />
</div>

{/* CHECK OUT */}

<div>
  <div
    style={{
      marginBottom: 6,
      fontWeight: 600,
    }}
  >
    Check Out
  </div>

  <TimePicker
    format="HH:mm"
    value={checkOutTime}
    needConfirm={false}
    disabledHours={() => {
      if (!checkInTime) return [];

      const startHour = checkInTime.hour();

      // Disable previous and same hours
      return [...Array(startHour + 1).keys()];
    }}
    disabledMinutes={(selectedHour) => {
      if (!checkInTime) return [];

      const startHour = checkInTime.hour();
      const startMinute = checkInTime.minute();

      // Disable previous minutes in same hour
      if (selectedHour === startHour) {
        return [...Array(startMinute + 1).keys()];
      }

      return [];
    }}
    onChange={(value) => setCheckOutTime(value)}
  />
</div>
      </div>

      {/* ================= TABLE ================= */}

      <Table
    
        columns={columns}
        dataSource={tableData}
        pagination={false}
      />

      {/* ================= FOOTER ================= */}

      <div
        style={{
          display: 'flex',
          justifyContent:
            'flex-end',
          marginTop: 20,
          gap: 10,
        }}
      >
        <Button onClick={onClose}>
          Cancel
        </Button>

        <Button
          type="primary"
          loading={submitLoading}
          onClick={handleSubmit}
        >
          {isEdit
            ? 'Update Attendance'
            : 'Submit Attendance'}
        </Button>
      </div>
    </Modal>
  );
};

export default AttendancePopup;